/**
 * RBAC Role-Permission Matrix — Logic
 *
 * Re-exports the ROLE_PERMISSION_MATRIX constant and provides utility
 * functions for permission resolution and access level lookup.
 *
 * The matrix data lives in role-matrix-data.ts (data-only, ~500 lines).
 * This module stays under the 300-line file size standard.
 *
 * Source of truth: docs/rbac/05-role-permission-matrix.md
 */

import type { PermissionKey } from './permissions';
import type { UserRole, AccessLevel } from './roles';
import { ROLE_PERMISSION_MATRIX } from './role-matrix-data';

// Re-export so consumers can still import from 'role-matrix'
export { ROLE_PERMISSION_MATRIX };

// ---------------------------------------------------------------------------
// resolvePermissions — resolve all non-'not_allowed' permissions for a role
// ---------------------------------------------------------------------------

/**
 * Resolve all permissions for a role, excluding those with `'not_allowed'` access.
 *
 * @param role - The user role to resolve permissions for
 * @returns A Set of PermissionKey values the role has some level of access to
 */
export function resolvePermissions(role: UserRole): Set<PermissionKey> {
  const matrix = ROLE_PERMISSION_MATRIX[role];
  const result = new Set<PermissionKey>();

  for (const [key, level] of Object.entries(matrix)) {
    if (level !== 'not_allowed') {
      result.add(key as PermissionKey);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// getAccessLevel — look up the access level for a role/permission pair
// ---------------------------------------------------------------------------

/**
 * Get the access level for a specific role and permission combination.
 * Returns `'not_allowed'` if the role or permission is not found in the matrix.
 *
 * @param role - The user role
 * @param permission - The permission key to check
 * @returns The AccessLevel for the role/permission pair
 */
export function getAccessLevel(role: UserRole, permission: PermissionKey): AccessLevel {
  return ROLE_PERMISSION_MATRIX[role]?.[permission] ?? 'not_allowed';
}
